import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ROUTES } from '../utils/constants';
import apiService from '../services/api';

const PurchasesPage = () => {
  const { user } = useAuth();
  const [purchasedContent, setPurchasedContent] = useState([]);
  const [manualPayments, setManualPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all'); // all, course, storybook
  const [filterSource, setFilterSource] = useState('all'); // all, individual, bundle, subscription
  const [searchQuery, setSearchQuery] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    loadData();
    loadUnreadCount();
  }, []);

  const loadUnreadCount = async () => {
    try {
      const readNotificationIds = JSON.parse(localStorage.getItem('readNotifications') || '[]');
      const generatedNotifications = [];
      
      const purchases = await apiService.getUserPurchases();
      purchases.forEach((purchase) => {
        const notifId = `purchase-${purchase.id}`;
        generatedNotifications.push({
          id: notifId,
          read: readNotificationIds.includes(notifId)
        });
      });

      const manualPayments = await apiService.getUserManualPayments();
      manualPayments.forEach((payment) => {
        const notifId = `payment-${payment.id}`;
        generatedNotifications.push({
          id: notifId,
          read: readNotificationIds.includes(notifId)
        });
      });

      const unread = generatedNotifications.filter(n => !n.read).length;
      setUnreadCount(unread);
    } catch (err) {
      console.error('Error loading notification count:', err);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const purchases = await apiService.getUserPurchases();
      setPurchasedContent(purchases);

      try {
        const payments = await apiService.getUserManualPayments();
        setManualPayments(payments);
      } catch (err) {
        console.error('Error loading manual payments:', err);
      }
    } catch (err) {
      console.error('Error loading purchases:', err);
    } finally {
      setLoading(false);
    }
  };

  const renderPurchaseTypeBadge = (item) => {
    if (!item.purchase_type || item.purchase_type === 'individual') {
      return (
        <div className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-bold">
          <i className="fas fa-shopping-cart"></i>
          <span>مشترى منفرد</span>
          <span className="text-green-600">(دائم)</span>
        </div>
      );
    }

    switch (item.purchase_type) {
      case 'subscription':
        return (
          <div className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-bold">
            <i className="fas fa-calendar-check"></i>
            <span>الاشتراك الشهري</span>
            <span className="text-orange-600">(مؤقت)</span>
          </div>
        );
      case 'bundle':
        return (
          <div className="inline-flex items-center gap-1 px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-bold">
            <i className="fas fa-gift"></i>
            <span>ضمن الحزمة #{item.bundle_id || '?'}</span>
            <span className="text-green-600">(دائم)</span>
          </div>
        );
      default:
        return null;
    }
  };

  // Filter purchases
  const filteredPurchases = purchasedContent.filter(item => {
    // Filter by type
    if (filterType !== 'all' && item.content_type !== filterType) return false;
    
    // Filter by source
    if (filterSource !== 'all') {
      const itemSource = item.purchase_type || 'individual';
      if (itemSource !== filterSource) return false;
    }
    
    // Filter by search query
    if (searchQuery) {
      const title = item.title_ar || item.title || '';
      if (!title.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }
    }
    
    return true;
  }).sort((a, b) => {
    // Sort by purchase date, newest first
    const dateA = new Date(a.purchased_at || a.purchase_date || a.created_at || 0);
    const dateB = new Date(b.purchased_at || b.purchase_date || b.created_at || 0);
    return dateB - dateA; // Descending order (newest first)
  });

  if (loading) {
    return (
      <div className="relative z-10 min-h-screen flex items-center justify-center bg-bg">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-text3">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative z-10 py-12 md:py-20 bg-gradient-to-b from-bg to-white min-h-screen">
      <div className="container mx-auto px-3 md:px-4">
        <div className="max-w-[1200px] mx-auto">
          {/* Header */}
          <div className="mb-8 md:mb-12">
            <Link
              to={ROUTES.DASHBOARD}
              className="inline-flex items-center gap-2 text-primary hover:underline font-bold mb-3 md:mb-4 text-sm md:text-base"
            >
              <i className="fas fa-arrow-right"></i>
              العودة للوحة التحكم
            </Link>
            <h1 className="text-2xl md:text-4xl lg:text-5xl font-black text-text mb-2 flex items-center gap-2 md:gap-3">
              <i className="fas fa-shopping-bag text-primary text-xl md:text-3xl"></i>
              جميع مشترياتي
            </h1>
            <p className="text-sm md:text-lg text-text3">سجل كامل لكل المشتريات والمعاملات</p>
          </div>

          {/* Filters - Compact for mobile */}
          <div className="bg-white rounded-2xl md:rounded-3xl p-3 md:p-6 shadow-custom mb-6 md:mb-8">
            <div className="flex gap-2 md:gap-4">
              {/* Search */}
              <div className="flex-1">
                <label className="hidden md:block text-sm font-bold text-text mb-2">البحث</label>
                <div className="relative">
                  <i className="fas fa-search absolute right-3 top-1/2 -translate-y-1/2 text-text3 text-sm md:text-base"></i>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="ابحث عن محتوى..."
                    className="w-full pr-9 md:pr-10 pl-2 md:pl-4 py-2 md:py-2 border-2 border-gray-200 rounded-lg md:rounded-xl focus:border-primary focus:outline-none text-sm md:text-base"
                  />
                </div>
              </div>

              {/* Filter by Type */}
              <div className="w-28 md:w-auto md:flex-1">
                <label className="hidden md:block text-sm font-bold text-text mb-2">نوع المحتوى</label>
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="w-full px-2 md:px-4 py-2 border-2 border-gray-200 rounded-lg md:rounded-xl focus:border-primary focus:outline-none text-sm md:text-base"
                >
                  <option value="all">الكل</option>
                  <option value="course">دورات</option>
                  <option value="storybook">قصص</option>
                </select>
              </div>

              {/* Filter by Source */}
              <div className="w-28 md:w-auto md:flex-1">
                <label className="hidden md:block text-sm font-bold text-text mb-2">طريقة الشراء</label>
                <select
                  value={filterSource}
                  onChange={(e) => setFilterSource(e.target.value)}
                  className="w-full px-2 md:px-4 py-2 border-2 border-gray-200 rounded-lg md:rounded-xl focus:border-primary focus:outline-none text-sm md:text-base"
                >
                  <option value="all">الكل</option>
                  <option value="individual">منفرد</option>
                  <option value="bundle">حزمة</option>
                  <option value="subscription">اشتراك</option>
                </select>
              </div>
            </div>
          </div>

          {/* Results Summary */}
          <div className="mb-4 md:mb-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 md:gap-0">
            <p className="text-sm md:text-base text-text2">
              عرض <span className="font-bold text-primary">{filteredPurchases.length}</span> من أصل{' '}
              <span className="font-bold">{purchasedContent.length}</span> مشتريات
            </p>
            <div className="text-left">
              <div className="text-xs md:text-sm text-text3">إجمالي المصروفات</div>
              <div className="text-xl md:text-2xl font-black text-primary">
                {purchasedContent.reduce((sum, item) => sum + (item.price || 1500), 0)} دج
              </div>
            </div>
          </div>

          {/* Purchases List */}
          <div className="bg-white rounded-2xl md:rounded-3xl p-4 md:p-6 lg:p-8 shadow-custom">
            {filteredPurchases.length > 0 ? (
              <div className="space-y-4">
                {filteredPurchases.map((item, index) => (
                  <div
                    key={`purchase-${item.id}`}
                    className="flex items-center justify-between p-4 border-b border-gray-100 last:border-0 hover:bg-gray-50 rounded-xl transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-primary to-primary-dark rounded-xl flex items-center justify-center text-white font-bold">
                        #{purchasedContent.length - purchasedContent.indexOf(item)}
                      </div>
                      {item.cover_image && (
                        <img
                          src={item.cover_image}
                          alt={item.title}
                          className="w-16 h-16 object-cover rounded-lg"
                        />
                      )}
                      <div>
                        <h4 className="font-bold text-text">{item.title_ar || item.title || 'محتوى غير معروف'}</h4>
                        <p className="text-sm text-text3">
                          {new Date(item.purchased_at || item.purchase_date || item.created_at).toLocaleDateString('ar-DZ', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </p>
                        <div className="mt-1">
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            item.content_type === 'course' 
                              ? 'bg-blue-100 text-blue-700' 
                              : 'bg-purple-100 text-purple-700'
                          }`}>
                            {item.content_type === 'course' ? 'دورة' : 'قصة'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="text-left">
                      <div className="text-xl font-black text-primary mb-2">
                        {item.price || 1500} دج
                      </div>
                      {renderPurchaseTypeBadge(item)}
                      <div className="mt-2">
                        <Link
                          to={item.content_type === 'course' ? `/course/${item.id}/view` : `/storybook/${item.id}/view`}
                          className="text-sm text-primary hover:underline font-bold"
                        >
                          ابدأ التعلم →
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <i className="fas fa-search text-6xl text-text3 mb-4"></i>
                <p className="text-xl text-text3">لا توجد نتائج مطابقة للبحث</p>
              </div>
            )}
          </div>

          {/* Manual Payments Section */}
          {manualPayments.length > 0 && (
            <div className="mt-8 bg-white rounded-3xl p-6 md:p-8 shadow-custom">
              <h2 className="text-2xl font-black text-text mb-6 flex items-center gap-2">
                <i className="fas fa-file-invoice text-primary"></i>
                طلبات الدفع اليدوي (CCP) - {manualPayments.length} طلب
              </h2>
              <div className="space-y-3">
                {manualPayments.map((payment) => (
                  <div
                    key={`manual-${payment.id}`}
                    className={`flex items-center justify-between p-4 rounded-xl border-2 ${
                      payment.status === 'approved'
                        ? 'bg-green-50 border-green-200'
                        : payment.status === 'rejected'
                        ? 'bg-red-50 border-red-200'
                        : 'bg-yellow-50 border-yellow-200'
                    }`}
                  >
                    <div className="flex items-center gap-4 flex-1">
                      {payment.cover_image && (
                        <img
                          src={payment.cover_image}
                          alt={payment.course_title}
                          className="w-12 h-12 object-cover rounded-lg"
                        />
                      )}
                      <div>
                        <h4 className="font-bold text-text">{payment.course_title}</h4>
                        <p className="text-sm text-text3">
                          {new Date(payment.created_at).toLocaleDateString('ar-DZ', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </p>
                      </div>
                    </div>
                    <div className="text-left">
                      <div className="text-lg font-black text-primary mb-1">{payment.amount} دج</div>
                      <div
                        className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold ${
                          payment.status === 'approved'
                            ? 'bg-green-100 text-green-700'
                            : payment.status === 'rejected'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-yellow-100 text-yellow-700'
                        }`}
                      >
                        {payment.status === 'approved' && '✓ مقبول'}
                        {payment.status === 'rejected' && '✗ مرفوض'}
                        {payment.status === 'pending' && 'قيد المراجعة'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Fixed Bottom Navigation - Mobile Only */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-2xl z-50 safe-area-bottom">
        <div className="grid grid-cols-3 gap-1 px-2 py-1">
          <Link
            to="/notifications"
            className="flex flex-col items-center justify-center py-1.5 px-2 rounded-lg hover:bg-gray-100 transition-colors relative"
          >
            <i className="fas fa-bell text-lg text-text2 mb-0.5"></i>
            <span className="text-[9px] font-bold text-text2">الإشعارات</span>
            {unreadCount > 0 && (
              <span className="absolute top-0.5 right-0.5 w-4 h-4 bg-red-500 text-white text-[8px] font-bold rounded-full flex items-center justify-center">
                {unreadCount}
              </span>
            )}
          </Link>
          
          <Link
            to={ROUTES.DASHBOARD}
            className="flex flex-col items-center justify-center py-1.5 px-2 rounded-lg hover:bg-gray-100 transition-colors relative"
          >
            <i className="fas fa-home text-lg text-text2 mb-0.5"></i>
            <span className="text-[9px] font-bold text-text2">لوحة التحكم</span>
          </Link>
          
          <Link
            to="/profile"
            className="flex flex-col items-center justify-center py-1.5 px-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <i className="fas fa-user text-lg text-text2 mb-0.5"></i>
            <span className="text-[9px] font-bold text-text2">الملف الشخصي</span>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PurchasesPage;
